import { createContext, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api, resolveImage } from "@/lib/api";

const SeoContext = createContext({ global: {}, pages: {} });

export function SeoProvider({ children }) {
  const [data, setData] = useState({ global: {}, pages: {} });
  useEffect(() => {
    api.get("/seo").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  return <SeoContext.Provider value={data}>{children}</SeoContext.Provider>;
}

export const useSeo = () => useContext(SeoContext);

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

const absolute = (url) => {
  if (!url) return "";
  const resolved = resolveImage(url);
  return resolved.startsWith("http") ? resolved : `${origin()}${resolved}`;
};

function buildLocalBusiness(g, siteName, image) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: siteName,
    description: g.meta_description || "",
    url: origin(),
    logo: image,
    image,
  };
  if (g.telephone) ld.telephone = g.telephone;
  if (g.street_address || g.city || g.postal_code) {
    ld.address = {
      "@type": "PostalAddress",
      streetAddress: g.street_address || undefined,
      postalCode: g.postal_code || undefined,
      addressLocality: g.city || undefined,
      addressRegion: g.region || undefined,
      addressCountry: "FR",
    };
  }
  const sameAs = [g.facebook_url, g.instagram_url, g.youtube_url].filter(Boolean);
  if (sameAs.length) ld.sameAs = sameAs;
  return ld;
}

export function Seo({ pageKey, title, description, keywords, image, path, jsonLd, type = "website" }) {
  const { global: g = {}, pages = {} } = useSeo();
  const page = pageKey ? pages[pageKey] : null;
  const siteName = g.site_name || "Pétales de Miel";

  const finalTitle = title || page?.title || siteName;
  const finalDesc = description || page?.description || g.meta_description || "";
  const finalKeywords = keywords || page?.keywords || "";
  const finalImage = absolute(image || page?.image_url || g.default_image_url || "/favicon.png");
  const canonical = `${origin()}${path || page?.path || (typeof window !== "undefined" ? window.location.pathname : "")}`;

  const lds = [];
  if (jsonLd) Array.isArray(jsonLd) ? lds.push(...jsonLd) : lds.push(jsonLd);
  if (pageKey === "home") lds.push(buildLocalBusiness(g, siteName, finalImage));

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="fr_FR" />
      {lds.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
